import { useQuery } from '@tanstack/react-query';
import { rows, type OrgUnit } from '@hrms/shared';
import { api } from '../../../lib/api';
import type { Option } from '../../../ui';

const opt = (list: OrgUnit[]): Option<number>[] => list.map((u) => ({ value: u.id, label: u.code ? `${u.name} (${u.code})` : u.name }));

/** Loads every org master for dropdowns. Cached under ['org', kind, 'all']. */
export function useOrgOptions(enabled = true) {
  const le = useQuery({ queryKey: ['org', 'legalEntities', 'all'], queryFn: () => api.org.legalEntities.list({ per_page: 500 }), enabled });
  const loc = useQuery({ queryKey: ['org', 'locations', 'all'], queryFn: () => api.org.locations.list({ per_page: 500 }), enabled });
  const dep = useQuery({ queryKey: ['org', 'departments', 'all'], queryFn: () => api.org.departments.list({ per_page: 500 }), enabled });
  const des = useQuery({ queryKey: ['org', 'designations', 'all'], queryFn: () => api.org.designations.list({ per_page: 500 }), enabled });
  const gr = useQuery({ queryKey: ['org', 'grades', 'all'], queryFn: () => api.org.grades.list({ per_page: 500 }), enabled });
  const cc = useQuery({ queryKey: ['org', 'costCentres', 'all'], queryFn: () => api.org.costCentres.list({ per_page: 500 }), enabled });
  return {
    legalEntities: opt(rows(le.data)),
    locations: opt(rows(loc.data)),
    departments: opt(rows(dep.data)),
    designations: opt(rows(des.data)),
    grades: opt(rows(gr.data)),
    costCentres: opt(rows(cc.data)),
  };
}

/** Label for an option value ('—' when unknown). */
export const optLabel = (opts: Option<number>[], id: unknown) => opts.find((o) => o.value === id)?.label ?? '—';

export const INDIAN_STATES: { code: string; name: string }[] = [
  ['AN', 'Andaman & Nicobar'], ['AP', 'Andhra Pradesh'], ['AR', 'Arunachal Pradesh'], ['AS', 'Assam'], ['BR', 'Bihar'],
  ['CH', 'Chandigarh'], ['CG', 'Chhattisgarh'], ['DN', 'Dadra & Nagar Haveli and Daman & Diu'], ['DL', 'Delhi'], ['GA', 'Goa'],
  ['GJ', 'Gujarat'], ['HR', 'Haryana'], ['HP', 'Himachal Pradesh'], ['JK', 'Jammu & Kashmir'], ['JH', 'Jharkhand'],
  ['KA', 'Karnataka'], ['KL', 'Kerala'], ['LA', 'Ladakh'], ['LD', 'Lakshadweep'], ['MP', 'Madhya Pradesh'],
  ['MH', 'Maharashtra'], ['MN', 'Manipur'], ['ML', 'Meghalaya'], ['MZ', 'Mizoram'], ['NL', 'Nagaland'],
  ['OD', 'Odisha'], ['PY', 'Puducherry'], ['PB', 'Punjab'], ['RJ', 'Rajasthan'], ['SK', 'Sikkim'],
  ['TN', 'Tamil Nadu'], ['TS', 'Telangana'], ['TR', 'Tripura'], ['UP', 'Uttar Pradesh'], ['UK', 'Uttarakhand'], ['WB', 'West Bengal'],
].map(([code, name]) => ({ code, name }));

export const stateOptions: Option<string>[] = INDIAN_STATES.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }));
